'use client';

import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/shadcn';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { Link } from '../../../../../../Link/Link';
import { toast } from '../../../../../../Toast/Toast';
import type { PaymentMethod } from '../../../../types';
import { type DeletePaymentMethodErrorReason, useDeletePaymentMethod } from '../../hooks/useDeletePaymentMethod';

type DeletePaymentMethodDialogProps = {
  paymentMethod: PaymentMethod;
  namespace: string;
  isLastPaymentMethod: boolean;
  onClose: () => void;
};

type DialogView = DeletePaymentMethodErrorReason | 'confirm';

type DialogViewConfig = {
  i18nKey: string;
  trackContainer: string;
  message?: ReactNode;
  actions: ReactNode;
};

function DeletePaymentMethodDialog({ paymentMethod, namespace, isLastPaymentMethod, onClose }: DeletePaymentMethodDialogProps) {
  const t = useTranslations(namespace);
  const [errorReason, setErrorReason] = useState<DeletePaymentMethodErrorReason | null>(null);

  const { deletePaymentMethod, isPending } = useDeletePaymentMethod({
    instrumentId: paymentMethod.id,
    onSuccess: () => {
      toast(t('delete_success_toast'), { type: 'card', trackType: 'Confirmation' }, { 'data-track-action': 'Payment Method Deleted', 'data-track-method': paymentMethod.name });
      onClose();
    },
    onError: setErrorReason,
  });

  // The API always rejects deleting the last payment method, so skip the request and show the error straight away
  const handleConfirm = () => (isLastPaymentMethod ? setErrorReason('last_payment_method') : deletePaymentMethod());

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPending) {
      onClose();
    }
  };

  const viewConfig: Record<DialogView, DialogViewConfig> = {
    confirm: {
      i18nKey: 'delete_modal',
      trackContainer: 'Payment Method Delete',
      actions: (
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending} data-track-name="Cancel" data-testid="delete-payment-method-cancel-button">
            {t('delete_modal.cancel_button')}
          </Button>
          <Button onClick={handleConfirm} disabled={isPending} data-track-name="Delete" data-testid="delete-payment-method-confirm-button">
            {t('delete_modal.delete_button')}
          </Button>
        </>
      ),
    },
    unknown: {
      i18nKey: 'delete_error',
      trackContainer: 'Payment Method Delete Error',
      actions: (
        <Button onClick={deletePaymentMethod} disabled={isPending} data-track-name="Try Again" data-testid="delete-payment-method-retry-button">
          {t('delete_error.try_again_button')}
        </Button>
      ),
    },
    last_payment_method: {
      i18nKey: 'delete_last_payment_method_error',
      trackContainer: 'Payment Method Delete Last Method Error',
      message: t.rich('delete_last_payment_method_error.message', {
        link: chunks => (
          <Link href={ROUTES.HELP_CENTRE.CHAT} data-testid="delete-payment-method-support-link" data-track-name="Customer Support">
            {chunks}
          </Link>
        ),
      }),
      actions: (
        <Button onClick={onClose} data-track-name="Close" data-testid="delete-payment-method-close-button">
          {t('delete_last_payment_method_error.close_button')}
        </Button>
      ),
    },
  };

  const { i18nKey, trackContainer, message, actions } = viewConfig[errorReason ?? 'confirm'];

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent variant="dialog" data-testid="delete-payment-method-dialog" data-track-feature="My Account" data-track-container={trackContainer}>
        <DialogHeader>
          <DialogTitle>{t(`${i18nKey}.heading`, { number: paymentMethod.number })}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{message ?? t(`${i18nKey}.message`)}</DialogDescription>
        <DialogFooter data-track-container="Actions">{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { DeletePaymentMethodDialog, type DeletePaymentMethodDialogProps };
